/**
 * Shared date formatters used across dashboard components.
 */

/**
 * Uzbek Latin month abbreviations. The `Intl.DateTimeFormat` implementation for
 * "uz-UZ" is incomplete in some browser/ICU builds and produces nonsensical
 * output like "2026 M04 8" instead of "8-apr, 2026". We supply the month names
 * directly to guarantee correct output.
 */
const UZ_MONTHS_SHORT = ["yan", "fev", "mar", "apr", "may", "iyn", "iyul", "avg", "sen", "okt", "noy", "dek"];

function isUzLatinLocale(locale: string): boolean {
  return locale === "uz-UZ" || locale === "uz";
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Returns "D-mon, YYYY" for the given Date using Uzbek Latin month abbreviations. */
function formatUzLatin(date: Date): string {
  return `${date.getDate()}-${UZ_MONTHS_SHORT[date.getMonth()]}, ${date.getFullYear()}`;
}

/** Returns "mon YYYY" for the given Date using Uzbek Latin month abbreviations. */
function formatUzLatinMonthYear(date: Date): string {
  return `${UZ_MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

/** Returns "D-mon, YYYY, HH:MM" for the given Date using Uzbek Latin month abbreviations. */
function formatUzLatinDateTime(date: Date): string {
  return `${formatUzLatin(date)}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Returns "D-mon" for the given Date using Uzbek Latin month abbreviations. */
function formatUzLatinShort(date: Date): string {
  return `${date.getDate()}-${UZ_MONTHS_SHORT[date.getMonth()]}`;
}

/** Format a date as "Mon YYYY" (e.g. "yan 2025" / "янв. 2025" / "Jan 2025"). */
export function formatExpiryDate(value: string | null | undefined, locale: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  if (isUzLatinLocale(locale)) return formatUzLatinMonthYear(date);
  return new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" })
    .format(date)
    .replace(/\s*г\.$/, "")
    .trim();
}

/** Format a date as medium style, omitting the year if it is the current year. */
export function formatIssueDate(value: string | null | undefined, locale: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  if (isUzLatinLocale(locale)) return isCurrentYear ? formatUzLatinShort(date) : formatUzLatin(date);
  return isCurrentYear
    ? new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date)
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

/** Format a timestamp as medium date + short time (e.g. "5-yan, 2025, 14:30"). */
export function formatDateTime(value: number | string | null | undefined, locale: string): string {
  if (value == null || value === "") return "";
  const date = typeof value === "number" ? new Date(value) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  if (isUzLatinLocale(locale)) return formatUzLatinDateTime(date);
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

/** Format a date as short month + day without year (e.g. "5-yan" / "Jan 5"). */
export function formatShortMonthDay(value: string | null | undefined, locale: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  if (isUzLatinLocale(locale)) return formatUzLatinShort(date);
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date);
}

/** Format a date as "D mon. YYYY" (e.g. "26 mar. 2026" / "26 мар. 2026"). */
export function formatDayMonthYear(value: string | null | undefined, locale: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.getDate();
  const year = date.getFullYear();
  if (isUzLatinLocale(locale)) {
    return `${day} ${UZ_MONTHS_SHORT[date.getMonth()]}. ${year}`;
  }
  const month = new Intl.DateTimeFormat(locale, { month: "short" })
    .format(date)
    .replace(/\.$/, "")
    .replace(/\s*г\.?$/, "")
    .trim()
    .toLowerCase();
  return `${day} ${month}. ${year}`;
}

export function getMonthsUntil(value: string): number | null {
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  return (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
}
